import { computed, ref } from 'vue'
import type { Router } from 'vue-router'

const routeBusy = ref(false)
const routeBusyLabelKey = ref('common.loading')
let routeBusyGeneration = 0
let routeBusyClearTimer: ReturnType<typeof setTimeout> | null = null

function screenKey(path: string, pageKey: unknown): string {
  return String(pageKey ?? path)
}

/** Read-only state for the app-wide route-change overlay. */
export function useRouteBusyState() {
  return {
    show: computed(() => routeBusy.value),
    labelKey: computed(() => routeBusyLabelKey.value),
  }
}

/**
 * Hold route overlay until the next screen has had time to mount its own loader.
 * Longer grace avoids a clear gap (blink) between route busy and page busy.
 */
export function registerRouteBusy(router: Router): void {
  router.beforeEach((to, from) => {
    if (
      from.matched.length > 0
      && screenKey(to.path, to.meta.pageKey) === screenKey(from.path, from.meta.pageKey)
      && to.name === from.name
    ) {
      return true
    }

    if (routeBusyClearTimer != null) {
      clearTimeout(routeBusyClearTimer)
      routeBusyClearTimer = null
    }

    routeBusyGeneration += 1
    routeBusy.value = true
    routeBusyLabelKey.value = 'common.loading'
    return true
  })

  router.afterEach(() => {
    const generation = routeBusyGeneration
    routeBusyClearTimer = setTimeout(() => {
      routeBusyClearTimer = null
      if (generation === routeBusyGeneration) {
        routeBusy.value = false
      }
    }, 420)
  })

  router.onError(() => {
    if (routeBusyClearTimer != null) {
      clearTimeout(routeBusyClearTimer)
      routeBusyClearTimer = null
    }
    routeBusy.value = false
  })
}
